import React, { useMemo, useState } from 'react';
import { Phone, Mail, MapPin, Clock, Upload, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * EOS-konformes Kontaktformular:
 * - nicht "Kontakt", sondern "Projekt klären"
 * - strukturierte Erstklärung statt Freitext-only
 * - klare Hürde, keine Marketing-Sprache
 *
 * Versand: mailto (ohne Backend). Später leicht auf API-Endpoint umstellbar.
 */
const ContactSection = ({ contact }) => {
  const scopeOptions = useMemo(
    () => [
      { key: 'elektrotechnik', label: 'Elektrotechnik' },
      { key: 'pv', label: 'Photovoltaik' },
      { key: 'speicher', label: 'Speicher' },
      { key: 'energiemanagement', label: 'Energiemanagement' },
      { key: 'sicherheit', label: 'Sicherheitstechnik' },
      { key: 'service', label: 'Betrieb / Service' },
    ],
    []
  );

  const [form, setForm] = useState({
    companyOrName: '',
    email: '',
    phone: '',
    projectType: 'Wohnbau',
    plz: '',
    status: 'Idee / Bedarf',
    timeframe: '0–3 Monate',
    scope: {
      elektrotechnik: true,
      pv: false,
      speicher: false,
      energiemanagement: false,
      sicherheit: false,
      service: false,
    },
    notes: '',
    fileName: '', // nur Anzeige (kein Upload ohne Backend)
    consent: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const toggleScope = (key) =>
    setForm((p) => ({ ...p, scope: { ...p.scope, [key]: !p.scope[key] } }));

  const validate = () => {
    const e = {};
    if (!String(form.email).trim()) e.email = 'E-Mail ist erforderlich.';
    if (!String(form.plz).trim()) e.plz = 'PLZ/Ort ist erforderlich.';
    if (!form.consent) e.consent = 'Zustimmung ist erforderlich.';
    const anyScope = Object.values(form.scope).some(Boolean);
    if (!anyScope) e.scope = 'Mindestens ein Leistungsbereich auswählen.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildMailto = () => {
    const scopeSelected = scopeOptions
      .filter((o) => form.scope[o.key])
      .map((o) => `- ${o.label}`)
      .join('\n');

    const lines = [
      'Projektanfrage – strukturierte Erstklärung',
      '',
      `Name/Firma: ${form.companyOrName || '-'}`,
      `E-Mail: ${form.email || '-'}`,
      `Telefon: ${form.phone || '-'}`,
      '',
      `Projektart: ${form.projectType}`,
      `PLZ/Ort: ${form.plz}`,
      `Projektstatus: ${form.status}`,
      `Zeitrahmen: ${form.timeframe}`,
      '',
      'Leistungsumfang:',
      scopeSelected || '-',
      '',
      'Randbedingungen / Besonderheiten:',
      form.notes?.trim() ? form.notes.trim() : '-',
      '',
      form.fileName ? `Hinweis: Datei vorhanden (Name): ${form.fileName}` : '',
    ]
      .filter((l) => l !== '')
      .join('\n');

    const subject = encodeURIComponent('Projektanfrage – Erstklärung');
    const body = encodeURIComponent(lines);

    const to = contact?.email || 'info@eos-energy-systems.de';
    return `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(false);

    if (!validate()) return;

    // mailto als kontrollierter Prozessstart (ohne Backend)
    window.location.href = buildMailto();
    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      data-bg="dark"
      className="bg-eos-grauDunkel text-eos-weißFast py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Kopf */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <h2 className="text-4xl lg:text-5xl font-semibold leading-tight">
              Projekt klären.
              <br />
              Strukturiert.
            </h2>

            <p className="mt-6 text-eos-weißFast/70 text-lg leading-relaxed">
              Am Anfang steht kein Angebot.
              <br />
              Am Anfang steht eine strukturierte Erstklärung.
              <br />
              <br />
              Wir prüfen Anforderungen, Randbedingungen und Zielgrößen, bevor
              Lösungen entstehen.
            </p>

            {/* Kontakt-Kanäle (ruhig, technisch) */}
            <div className="mt-10 space-y-6">
              <div className="flex gap-4">
                <Phone className="mt-1 text-eos-weißFast/60" />
                <div>
                  <div className="text-sm text-eos-weißFast/50">Telefon</div>
                  <div className="mt-1">{contact?.phone}</div>
                </div>
              </div>

              <div className="flex gap-4">
                <Mail className="mt-1 text-eos-weißFast/60" />
                <div>
                  <div className="text-sm text-eos-weißFast/50">E-Mail</div>
                  <div className="mt-1">{contact?.email}</div>
                </div>
              </div>

              <div className="flex gap-4">
                <MapPin className="mt-1 text-eos-weißFast/60" />
                <div>
                  <div className="text-sm text-eos-weißFast/50">Standort</div>
                  <div className="mt-1 whitespace-pre-line">
                    {contact?.address}
                  </div>
                </div>
              </div>

              {contact?.hours ? (
                <div className="flex gap-4">
                  <Clock className="mt-1 text-eos-weißFast/60" />
                  <div>
                    <div className="text-sm text-eos-weißFast/50">
                      Erreichbarkeit
                    </div>
                    <div className="mt-1">{contact?.hours}</div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Formular */}
          <div className="lg:col-span-7">
            <div className="border border-white/10 bg-white/5 p-7 sm:p-8 rounded-none">
              <div className="mb-6">
                <div className="text-sm text-eos-weißFast/50">
                  Hinweis zur Anfrage
                </div>
                <p className="mt-2 text-eos-weißFast/70">
                  Dieses Formular dient der Ersteinschätzung. Ohne technische
                  Klärung entstehen keine belastbaren Angebote.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-7">
                {/* Basisdaten */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-eos-weißFast/60">
                      Name / Firma (optional)
                    </label>
                    <input
                      value={form.companyOrName}
                      onChange={(e) => setField('companyOrName', e.target.value)}
                      className="mt-2 w-full bg-transparent border border-white/10 px-4 py-3 text-eos-weißFast outline-none focus:border-white/25"
                      placeholder="z. B. Muster GmbH"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-eos-weißFast/60">
                      E-Mail *
                    </label>
                    <input
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      className="mt-2 w-full bg-transparent border border-white/10 px-4 py-3 text-eos-weißFast outline-none focus:border-white/25"
                      placeholder="name@domain.de"
                      type="email"
                    />
                    {errors.email ? (
                      <div className="mt-2 text-sm text-white/80">
                        {errors.email}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-sm text-eos-weißFast/60">
                      Telefon (optional)
                    </label>
                    <input
                      value={form.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                      className="mt-2 w-full bg-transparent border border-white/10 px-4 py-3 text-eos-weißFast outline-none focus:border-white/25"
                      placeholder="+49 …"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-eos-weißFast/60">
                      PLZ / Ort *
                    </label>
                    <input
                      value={form.plz}
                      onChange={(e) => setField('plz', e.target.value)}
                      className="mt-2 w-full bg-transparent border border-white/10 px-4 py-3 text-eos-weißFast outline-none focus:border-white/25"
                      placeholder="z. B. 78357 Mühlingen"
                    />
                    {errors.plz ? (
                      <div className="mt-2 text-sm text-white/80">
                        {errors.plz}
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Projektparameter */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm text-eos-weißFast/60">
                      Projektart
                    </label>
                    <select
                      value={form.projectType}
                      onChange={(e) => setField('projectType', e.target.value)}
                      className="mt-2 w-full bg-transparent border border-white/10 px-4 py-3 text-eos-weißFast outline-none focus:border-white/25"
                    >
                      <option className="bg-eos-grauDunkel" value="Wohnbau">
                        Wohnbau
                      </option>
                      <option className="bg-eos-grauDunkel" value="Gewerbe">
                        Gewerbe
                      </option>
                      <option className="bg-eos-grauDunkel" value="Bestand">
                        Bestand / Sanierung
                      </option>
                      <option className="bg-eos-grauDunkel" value="Neubau">
                        Neubau
                      </option>
                      <option className="bg-eos-grauDunkel" value="Sonstiges">
                        Sonstiges
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-eos-weißFast/60">
                      Projektstatus
                    </label>
                    <select
                      value={form.status}
                      onChange={(e) => setField('status', e.target.value)}
                      className="mt-2 w-full bg-transparent border border-white/10 px-4 py-3 text-eos-weißFast outline-none focus:border-white/25"
                    >
                      <option className="bg-eos-grauDunkel" value="Idee / Bedarf">
                        Idee / Bedarf
                      </option>
                      <option className="bg-eos-grauDunkel" value="Vorplanung">
                        Vorplanung
                      </option>
                      <option
                        className="bg-eos-grauDunkel"
                        value="Ausführungsplanung"
                      >
                        Ausführungsplanung
                      </option>
                      <option className="bg-eos-grauDunkel" value="Umsetzung">
                        Umsetzung
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-eos-weißFast/60">
                      Zeitrahmen
                    </label>
                    <select
                      value={form.timeframe}
                      onChange={(e) => setField('timeframe', e.target.value)}
                      className="mt-2 w-full bg-transparent border border-white/10 px-4 py-3 text-eos-weißFast outline-none focus:border-white/25"
                    >
                      <option className="bg-eos-grauDunkel" value="0–3 Monate">
                        0–3 Monate
                      </option>
                      <option className="bg-eos-grauDunkel" value="3–6 Monate">
                        3–6 Monate
                      </option>
                      <option className="bg-eos-grauDunkel" value="6–12 Monate">
                        6–12 Monate
                      </option>
                      <option
                        className="bg-eos-grauDunkel"
                        value="> 12 Monate"
                      >
                        &gt; 12 Monate
                      </option>
                    </select>
                  </div>
                </div>

                {/* Leistungsumfang */}
                <div>
                  <div className="flex items-baseline justify-between gap-4">
                    <label className="block text-sm text-eos-weißFast/60">
                      Leistungsumfang *
                    </label>
                    {errors.scope ? (
                      <div className="text-sm text-white/80">{errors.scope}</div>
                    ) : null}
                  </div>

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {scopeOptions.map((o) => (
                      <label
                        key={o.key}
                        className="flex items-center gap-3 border border-white/10 px-4 py-3 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={!!form.scope[o.key]}
                          onChange={() => toggleScope(o.key)}
                          className="accent-white"
                        />
                        <span className="text-eos-weißFast/80">{o.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Randbedingungen */}
                <div>
                  <label className="block text-sm text-eos-weißFast/60">
                    Randbedingungen / Besonderheiten
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                    className="mt-2 w-full min-h-[120px] bg-transparent border border-white/10 px-4 py-3 text-eos-weißFast outline-none focus:border-white/25"
                    placeholder="z. B. Anzahl Zähler, WP/Boiler, Ladeinfrastruktur, Netzanschluss, Brandschutz, Bestand/Leerrohre, gewünschte Dokumentation …"
                  />
                </div>

                {/* Datei (nur Name; echtes Upload braucht Backend) */}
                <div>
                  <label className="block text-sm text-eos-weißFast/60">
                    Plan / Skizze (optional)
                  </label>
                  <div className="mt-2 flex items-center gap-3 border border-white/10 px-4 py-3">
                    <Upload className="text-eos-weißFast/50" />
                    <input
                      type="file"
                      onChange={(e) =>
                        setField(
                          'fileName',
                          e.target.files?.[0]?.name || ''
                        )
                      }
                      className="w-full text-eos-weißFast/70 file:mr-4 file:px-4 file:py-2 file:border-0 file:bg-white/10 file:text-eos-weißFast file:rounded-none"
                    />
                  </div>
                  <div className="mt-2 text-sm text-eos-weißFast/45">
                    Upload wird derzeit nicht übertragen (ohne Backend). Der Dateiname wird in die Anfrage übernommen.
                  </div>
                </div>

                {/* Zustimmung */}
                <div className="border-t border-white/10 pt-6">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={() => setField('consent', !form.consent)}
                      className="mt-1 accent-white"
                    />
                    <span className="text-eos-weißFast/70 text-sm">
                      Ich stimme zu, dass meine Angaben zur Bearbeitung der Anfrage verwendet werden. *
                    </span>
                  </label>
                  {errors.consent ? (
                    <div className="mt-2 text-sm text-white/80">
                      {errors.consent}
                    </div>
                  ) : null}
                </div>

                {/* Submit */}
                <div className="flex items-center justify-between gap-6">
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-eos-weißFast text-eos-grauDunkel hover:bg-eos-weißFaster rounded-none px-6"
                  >
                    Projekt klären
                  </Button>

                  {submitted ? (
                    <div className="flex items-center gap-2 text-eos-weißFast/70">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm">E-Mail-Entwurf geöffnet.</span>
                    </div>
                  ) : null}
                </div>
              </form>
            </div>

            <div className="mt-6 text-sm text-eos-weißFast/45">
              Hinweis: Versand erfolgt über deinen E-Mail-Client (mailto). Für echten Upload / Ticketing ist ein API-Endpoint erforderlich.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
